import { stringify } from 'querystringify'

// Actions
import { actions as trackingActions } from 'modules/tracking/actions'
import { actions as browseActions } from 'modules/browse/actions'

// Selectors
import { selectLoopBarPositions } from 'modules/audio/selectors'
import {
  selectTrackIdsFromSearchQuery,
  selectTrackListFromTrackIds,
  selectPurgedSearchString,
  songIsCountryRestricted,
} from 'modules/browse/selectors'

import { ownProfileEntity } from 'selectors'

// Types
import { SimpleAction, Dispatch, GetState, APIAction, Loop, State, TrackList, QueryObject } from 'types'

// Utils
import { HOSTNAME, PREVIEW_LIMIT } from '@constants'
import { audioBackend } from 'modules/audio/backend'
import { timeToBarIndex, isLoopPlayerSupportedBrowser } from 'modules/audio/utilities'
import { createSongItemId } from 'utils'
import jsTracking from 'lib/jsTracking'

const DEFAULT_BAR_COUNT = 2

export const getNextSongAndPlaylist = (state: State, contextKey: string, fwd = true) => {
  const { audio } = state
  const { songItemId, playlists } = audio

  const {
    entities: { songs },
  } = state
  const profile = ownProfileEntity(state)

  const currentPlaylist = playlists[contextKey]
  if (currentPlaylist && currentPlaylist.length > 1) {
    const currentTrackIndex = currentPlaylist.findIndex(({ trackId, loop }) => createSongItemId(trackId, loop) === songItemId)

    let nextIndex = (currentTrackIndex + (fwd ? 1 : -1)) % currentPlaylist.length
    while (nextIndex !== currentTrackIndex) {
      const { songId } = currentPlaylist[nextIndex]
      const regionRestricted = songIsCountryRestricted(songs[songId], profile)
      if (!regionRestricted) {
        break
      }
      nextIndex = (nextIndex + (fwd ? 1 : -1)) % currentPlaylist.length
      if (nextIndex < 0) {
        nextIndex += currentPlaylist.length
      }
    }
    return currentPlaylist[nextIndex]
  }
  return null
}

const setLoopedLocalStorage = () => {
  if (!localStorage.getItem('hasLooped')) {
    localStorage.setItem('hasLooped', 'true')
  }
}

const restartToneContext = (nrRetry = 0): void => {
  setTimeout(() => {
    if (audioBackend?.tone) {
      audioBackend?.tone.start()
    } else if (nrRetry < 10) {
      restartToneContext(nrRetry + 1)
    }
  }, 100 + 100 * nrRetry)
}

export const actions = {
  fetchTrackFeatureData: (trackId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_TRACK_FEATURE_DATA',
      method: 'GET',
      endpoint: `/api/track/features/${trackId}/`,
      actionParams: {
        trackId,
      },
    },
  }),

  togglePaused: () => (dispatch: Dispatch, getState: GetState) => {
    const { audio } = getState()
    if (audio.isLoading) {
      return Promise.resolve()
    }
    audioBackend?.setPaused(!getState().audio.isPaused)
    const isPaused: boolean = audioBackend?.isPaused() || false
    return dispatch({
      type: 'AUDIO__SET_PAUSED',
      data: isPaused,
    })
  },

  reset: () => (dispatch: Dispatch) => {
    audioBackend?.setPaused(true)
    return dispatch({
      type: 'AUDIO__RESET',
    })
  },

  selectPlaylist: (contextKey: string): SimpleAction => ({
    type: 'AUDIO__SELECT_PLAYLIST',
    data: contextKey,
  }),

  setPlaylist: (playlist: TrackList, contextKey: string, setAsActivePlaylist: boolean): SimpleAction => ({
    type: 'AUDIO__SET_PLAYLIST',
    data: { playlist, contextKey, setAsActivePlaylist },
  }),

  setUrl: (url: string): SimpleAction => ({
    type: 'AUDIO__SET_URL',
    data: url,
  }),

  fetchTrack: (trackId: number) => ({
    CALL_API: {
      type: 'FETCH_TRACK',
      method: 'GET',
      endpoint: `/api/search/track/${trackId}/`,
    },
  }),

  clearPlaylist: (contextKey: string): SimpleAction => ({
    type: 'AUDIO__CLEAR_PLAYLIST',
    data: contextKey,
  }),

  fetchTrackPlayUrl: (trackId: number) => ({
    CALL_API: {
      type: 'FETCH_TRACK_PLAY_URL',
      method: 'GET',
      endpoint: `/api/song/playurl/${trackId}/`,
    },
  }),

  _playTrack: (songItemId: string, trackId: number, contextKey: string): SimpleAction => ({
    type: 'AUDIO__PLAY_TRACK',
    data: { songItemId, trackId, contextKey },
  }),

  updateAudioFilter: (values: [number, number]) => () => {
    audioBackend?.updateFilter(values)
  },

  updateVolume: (volume: number) => () => {
    audioBackend?.updateVolume(volume)
  },

  trackLoop: (loop: Loop) => (dispatch: Dispatch, getState: GetState) => {
    const { track_id, beat_start, beat_count, time_start, time_end } = loop
    dispatch(
      trackingActions.trackevent('Loop', {
        trackId: track_id,
        beat_start,
        beat_count,
        time_start,
        time_end,
        contextKey: getState().audio.currentPlaylist,
      }),
    )
  },

  setCurrentLoop: (loop: Loop) => async (dispatch: Dispatch, getState: GetState) => {
    const audioState = getState().audio
    const { track_id, beat_start, beat_count, time_start, time_end } = loop
    const trackId = track_id || audioState.trackId
    const beatPositions = audioState.features[String(trackId)]?.beat_positions
    const timeStart = time_start || beatPositions[beat_start]
    const timeEnd = time_end || beatPositions[beat_start + beat_count]
    const newLoop: Loop = {
      track_id: trackId,
      beat_start,
      beat_count,
      time_start: timeStart,
      time_end: timeEnd,
    }
    jsTracking('', {
      eventCategory: 'loop',
      eventAction: 'play',
      eventLabel: `${beat_start}:$beat_count}`,
      eventValue: trackId,
    })
    dispatch(actions.trackLoop(newLoop))
    return dispatch({
      type: 'AUDIO__SET_CURRENT_LOOP',
      data: newLoop,
    })
  },

  setIsLoading: (isLoading: boolean): SimpleAction => ({
    type: 'AUDIO__SET_IS_LOADING',
    data: isLoading,
  }),

  playPlaylist: (playlistContext: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState().audio
    if (state.isLoading) {
      return Promise.resolve()
    }
    const currentPlaylist = state.playlists[playlistContext]
    if (currentPlaylist) {
      const selectedTrack = currentPlaylist[0]
      if (!selectedTrack) {
        return console.log('no SelectedTrack for context', playlistContext) // eslint-disable-line
      }
      return dispatch(
        actions.playTrack(
          createSongItemId(selectedTrack.trackId, selectedTrack.loop),
          selectedTrack.trackId,
          playlistContext,
          true,
          selectedTrack.loop,
        ),
      )
    }
    return Promise.reject()
  },

  playTrack:
    (songItemId: string, trackId: number, contextKey: string, shouldSetPlaylist = true, loop?: Loop | undefined | null) =>
    async (dispatch: Dispatch, getState: GetState) => {
      const state = getState()
      if (!state.entities.tracks[trackId]) {
        dispatch(browseActions.fetchTrack(trackId))
      }

      if (state.audio.isLoading) {
        return Promise.resolve()
      }

      dispatch(actions.stopLoop(0))
      dispatch({ type: 'AUDIO__LOAD_START', data: { trackId, songItemId } })
      dispatch(actions.togglePaused())

      if (shouldSetPlaylist) {
        // Only needs to be set when called from component
        dispatch(actions.selectPlaylist(contextKey))

        // Udate the play url only if manually playing from component (pagination/next/skip shuld not change the url)
        // Save the url in state so pagination can use that value even if navigated away
        const { location } = state.router
        const url = `${location.pathname}?${location.search}`
        dispatch(actions.setUrl(url))
        dispatch(trackingActions.trackPlayTrack(trackId, contextKey, url))
      } else {
        const { url } = state.audio
        dispatch(trackingActions.trackPlayTrack(trackId, contextKey, url))
      }

      const [
        {
          data: { response: trackUrl },
        },
      ] = (await Promise.all([
        dispatch(actions.fetchTrackPlayUrl(trackId)),
        audioBackend?._loadTrack(`${HOSTNAME}/static/sounds/silence.mp3`),
        ...((loop
          ? [
              dispatch(actions.setCurrentLoop(loop)),
              dispatch({
                type: 'AUDIO__SET_SONG_ITEM_ID',
                data: songItemId,
              }),
            ]
          : []) as Promise<unknown>[]),
      ])) as [{ data: { response: string } }]

      if (state.featureToggles.audioFilter?.active) {
        audioBackend?.setupFilters()
      }

      await audioBackend?.play(trackUrl)
      if (loop) {
        await audioBackend?.loadTone()
        return dispatch(actions.turnOnLooping())
      }
      return dispatch(actions._playTrack(songItemId, trackId, contextKey))
    },

  playSearchQuery: (queryObj: QueryObject) => async (dispatch: Dispatch, getState: GetState) => {
    dispatch(actions.reset())
    dispatch({ type: 'PREPARE_PLAY_SEARCH_QUERY_START' })
    const contextKey = JSON.stringify(queryObj)
    dispatch(actions.setPlaylist([], contextKey, true)) // set empty playlist while loading for correct active loading state
    const searchQuery = selectPurgedSearchString(`${stringify({ ...queryObj })}`)

    return dispatch(browseActions.fetchTracksBySearchQuery(searchQuery)).then(() => {
      const state = getState()
      const trackIds = selectTrackIdsFromSearchQuery(state, searchQuery)
      const parsedtrackIds = trackIds.map(id => ({ id }))
      const trackList = selectTrackListFromTrackIds(state, parsedtrackIds)

      if (trackIds.length) {
        dispatch(actions.setPlaylist(trackList, contextKey, true)) // update with real tracklist
        dispatch({ type: 'PREPARE_PLAY_SEARCH_QUERY_DONE' })
        return dispatch(actions.playPlaylist(contextKey))
      }
      dispatch({ type: 'PREPARE_PLAY_SEARCH_QUERY_ERROR' })
      dispatch(actions.setPlaylist([], '', true)) // failed- no playlist should be active
      return alert('Found no tracks to play…')
    })
  },

  setLoopAtTimestamp: (trackId: number, timestamp: number) => async (dispatch: Dispatch, getState: GetState) => {
    const audioState = getState().audio
    const { currentLoop } = audioState
    const beatCount = currentLoop?.beat_count || audioState.features[String(trackId)]?.beats_per_bar * DEFAULT_BAR_COUNT
    const beatsPerBar = audioState.features[String(trackId)]?.beats_per_bar
    const barPositions = selectLoopBarPositions(getState(), trackId)
    const beatPositions = audioState.features[String(trackId)]?.beat_positions || []
    const beatStart = timeToBarIndex(timestamp, barPositions) * beatsPerBar + audioState.beatOffset
    const newLoop: Loop = {
      track_id: trackId,
      beat_start: beatStart,
      beat_count: beatCount,
    }

    if (currentLoop === newLoop || beatStart + beatCount > beatPositions.length - 1) {
      return Promise.resolve()
    }
    return dispatch(actions.setCurrentLoop(newLoop))
  },

  clear: () => (dispatch: Dispatch) => {
    audioBackend?.setPaused(true)
    dispatch({ type: 'AUDIO__SONG_CLEAR' })
  },

  skip:
    (fwd = false) =>
    (dispatch: Dispatch, getState: GetState) => {
      const state = getState()
      const audioState = state.audio
      if (audioState.isLoading) {
        return Promise.resolve()
      }
      const { currentPlaylist } = audioState
      const skipInfo = getNextSongAndPlaylist(state, currentPlaylist, fwd)
      if (skipInfo) {
        const { trackId, loop } = skipInfo
        return dispatch(actions.playTrack(createSongItemId(trackId, loop), trackId, currentPlaylist, false, loop))
      } else {
        return dispatch(actions.clear())
      }
    },

  endPlay: () => (dispatch: Dispatch) => {
    dispatch({ type: 'AUDIO__SONG_TRACK_END' })
    return dispatch(actions.skip(true))
  },

  seekToTime:
    (timestamp: number, addition = 0) =>
    async (dispatch: Dispatch, getState: GetState) => {
      const audioState = getState().audio
      if (audioState.isLoading) {
        return Promise.resolve()
      }

      if (audioState.isLooping) {
        const { trackId } = audioState
        const { currentLoop } = getState().audio
        const features = audioState.features[String(trackId)]
        const beatsPerBar = features.beats_per_bar
        const beatPositions = features.beat_positions
        const newLoopStartTimestamp = beatPositions[(currentLoop?.beat_start || 0) + beatsPerBar * (addition + 1)]
        await dispatch(actions.setLoopAtTimestamp(trackId, addition ? newLoopStartTimestamp : timestamp))
        const newLoop = getState().audio.currentLoop
        return audioBackend?.updateLoop(newLoop?.beat_start, newLoop?.beat_count)
      } else {
        return audioBackend?.seekTo(timestamp + addition)
      }
    },

  stopLoop: (position?: number) => (dispatch: Dispatch) => {
    let time = 0
    if (position === 0 || position) {
      time = 0
    } else if (audioBackend) {
      time = audioBackend.getCurrentLoopStartTime()
    }
    audioBackend?.stopLoop()
    dispatch({
      type: 'AUDIO__SET_IS_LOOPING',
      data: false,
    })
    return dispatch(actions.seekToTime(time))
  },

  toggleLoop: () => async (dispatch: Dispatch, getState: GetState) => {
    restartToneContext()
    if (!isLoopPlayerSupportedBrowser()) {
      alert('Please upgrade to the latest version of Safari to use the loop player.')
      return Promise.resolve()
    }
    await audioBackend?.loadTone()
    if (getState().audio.isLooping) {
      await dispatch(actions.togglePaused())
      await dispatch(actions.stopLoop())
      return dispatch(actions.togglePaused())
    } else {
      return dispatch(actions.turnOnLooping())
    }
  },

  setBeatOffset: (offset: number) => ({
    type: 'AUDIO__SET_BEAT_OFFSET',
    data: offset,
  }),

  turnOnLooping: () => async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(actions.setIsLoading(true))
    const audioState = getState().audio
    const { trackId } = audioState
    const trackFeatures = audioState.features[String(trackId)]
    setLoopedLocalStorage()
    if (!trackFeatures) {
      await dispatch(actions.fetchTrackFeatureData(trackId))
    }

    await dispatch(actions.togglePaused())

    const updatedAudioState = getState().audio
    if (!audioState.currentLoop) {
      const offset = updatedAudioState.features[String(trackId)]?.first_bar_at_beat
      await dispatch(actions.setBeatOffset(offset))
      await dispatch(actions.setLoopAtTimestamp(trackId, audioBackend?.getCurrentTime() || 0))
    } else {
      const loop = getState().audio.currentLoop
      const offset = (loop?.beat_start || 1) % updatedAudioState.features[String(trackId)]?.beats_per_bar
      await dispatch(actions.setBeatOffset(offset))
    }
    const features = updatedAudioState.features[String(trackId)]
    await audioBackend?.setBeatPositions(features.beat_positions)
    const loop = getState().audio.currentLoop
    if (loop) {
      await audioBackend?.createLoop(loop?.track_id || 0, loop.beat_start, loop.beat_count)
    }

    return dispatch({ type: 'AUDIO__START_PLAYING_LOOP' })
  },

  // Change current loop

  updateLoop: (beatStart: number, beatCount: number) => async (dispatch: Dispatch, getState: GetState) => {
    const state = getState().audio
    const { trackId, isPaused, currentLoop: loop, features, beatOffset } = state
    const beatPositions = features[String(trackId)]?.beat_positions || []
    const { isAuthenticated } = getState().auth
    let newLoop: Loop
    if (beatStart + beatCount > beatPositions.length - 1 || (!isAuthenticated && beatPositions[beatStart + beatCount] > PREVIEW_LIMIT)) {
      // TODO: jump to last bars instead of doing nothing if we arent already there.
      return Promise.resolve()
    } else if (beatStart <= beatOffset) {
      newLoop = { beat_start: beatOffset, beat_count: beatCount }
    } else {
      newLoop = { beat_start: beatStart, beat_count: beatCount }
    }

    if (isPaused) {
      dispatch({
        type: 'AUDIO__SET_PAUSED',
        data: false,
      })
    }

    if (loop === newLoop) {
      return Promise.resolve()
    }
    await audioBackend?.updateLoop(newLoop.beat_start, newLoop.beat_count)
    return dispatch(actions.setCurrentLoop(newLoop))
  },

  moveLoop: (numberOfBars: number) => async (dispatch: Dispatch, getState: GetState) => {
    const audioState = getState().audio
    if (audioState.isLoading) {
      return Promise.resolve()
    }
    const { beat_start, beat_count } = audioState.currentLoop || {}
    const { trackId } = audioState
    if (beat_start && beat_count) {
      const newBeatStart = Math.max(0, beat_start + numberOfBars * audioState.features[String(trackId)]?.beats_per_bar)
      return dispatch(actions.updateLoop(newBeatStart, beat_count))
    }
    return null
  },

  offsetBeat: (offset: number) => async (dispatch: Dispatch, getState: GetState) => {
    const audioState = getState().audio
    if (!audioState.currentLoop) {
      return false
    }
    if (audioState.isLoading) {
      return Promise.resolve()
    }
    const { beat_start, beat_count } = audioState.currentLoop
    const { trackId } = audioState
    const beatsLength = audioState.features[String(trackId)]?.beat_positions.length
    const beatEnd = beat_start + beat_count + offset
    if (beat_start + offset < 0 || beatEnd > beatsLength - 1) {
      return Promise.resolve()
    }
    const beatsPerBar = audioState.features[String(trackId)]?.beats_per_bar
    const newOffset = (audioState.beatOffset + offset + beatsPerBar) % beatsPerBar
    await dispatch(actions.setBeatOffset(newOffset))
    return dispatch(actions.updateLoop(beat_start + offset, beat_count))
  },

  setLoopBeatCount: (beatCount: number) => async (dispatch: Dispatch, getState: GetState) => {
    const audioState = getState().audio
    if (audioState.isLoading) {
      return Promise.resolve()
    }
    const { beat_start } = audioState.currentLoop || {}
    if (beat_start) {
      return dispatch(actions.updateLoop(beat_start, beatCount))
    }
    return null
  },

  setIsHotkeySeeking: (isHotkeySeeking: boolean): SimpleAction => ({
    type: 'AUDIO__SET_HOTKEY_SEEKING',
    data: isHotkeySeeking,
  }),
}
